<template>
  <el-card>
    <el-form :inline="true" @submit.prevent>
      <el-form-item>
        <el-input v-model.trim="keyword" placeholder="角色名/描述" @keyup.enter="onSearch" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearch">搜索</el-button>
        <el-button @click="onReset">重置</el-button>
        <el-button @click="openCreate">新增角色</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" stripe border v-loading="loading">
      <el-table-column prop="role_id" label="ID" width="80" />
      <el-table-column prop="name" label="名称" min-width="120" />
      <el-table-column label="分类" width="120">
        <template #default="{ row }">{{ categoryText(row.gender) }}</template>
      </el-table-column>
      <el-table-column label="头像" width="96">
        <template #default="{ row }">
          <el-image
            v-if="row.avatar"
            :src="fullAvatar(row.avatar)"
            :preview-src-list="[fullAvatar(row.avatar)]"
            fit="cover"
            style="width: 48px; height: 48px; border-radius: 8px; display: block;"
          />
          <el-avatar v-else :size="48">无</el-avatar>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="220" show-overflow-tooltip />
      <el-table-column prop="raw_tags" label="标签" min-width="160" show-overflow-tooltip />
      <el-table-column label="可见范围" min-width="220" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.is_public">全部用户可见</span>
          <span v-else>仅用户ID: {{ (row.visible_user_ids || []).join(', ') || '未配置' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-space>
            <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-button link type="danger" @click="remove(row.role_id)">删除</el-button>
          </el-space>
        </template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page="query.page"
        :page-size="query.page_size"
        @current-change="onPageChange"
      />
    </div>
  </el-card>

  <el-dialog v-model="dialogVisible" :title="editId ? '编辑角色' : '新增角色'" width="560px">
    <el-form label-position="top">
      <el-alert
        title="编辑时已自动填充原始数据，你可以直接修改并保存。"
        type="info"
        :closable="false"
        style="margin-bottom: 12px;"
      />
      <el-form-item label="头像上传">
        <el-space>
          <el-image
            v-if="form.avatar"
            :src="fullAvatar(form.avatar)"
            :preview-src-list="[fullAvatar(form.avatar)]"
            fit="cover"
            style="width: 72px; height: 72px; border-radius: 10px;"
          />
          <el-avatar v-else :size="72">无图</el-avatar>
          <el-upload
            :show-file-list="false"
            :auto-upload="false"
            accept="image/*"
            :on-change="onAvatarSelect"
          >
            <el-button>上传头像图片</el-button>
          </el-upload>
        </el-space>
        <el-text type="info">用途：用于角色列表和聊天详情展示形象。</el-text>
      </el-form-item>
      <el-form-item label="名称">
        <el-input v-model.trim="form.name" />
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="form.gender" style="width: 100%">
          <el-option value="male" label="男性角色" />
          <el-option value="female" label="女性角色" />
          <el-option value="other" label="其他" />
        </el-select>
        <el-text type="info">用途：用于角色分类展示，也会影响默认人设语气。</el-text>
      </el-form-item>
      <el-form-item label="描述">
        <el-input v-model.trim="form.description" type="textarea" :rows="4" />
      </el-form-item>
      <el-form-item label="开场白">
        <el-input v-model.trim="form.opening_message" placeholder="首次建会话时展示的开场语" />
      </el-form-item>
      <el-form-item label="说话风格">
        <el-input v-model.trim="form.speaking_style" type="textarea" :rows="3" placeholder="例如：温柔、简短、先共情后建议" />
      </el-form-item>
      <el-form-item label="角色音色ID">
        <el-input v-model.trim="form.tts_voice_type" placeholder="可选，留空则按角色名默认分配音色" />
        <el-text type="info">用途：该角色生成语音时优先使用此音色ID。</el-text>
      </el-form-item>
      <el-form-item label="边界规则">
        <el-input v-model.trim="form.boundary_rules" type="textarea" :rows="3" placeholder="例如：不做医疗诊断、不做投资承诺" />
      </el-form-item>
      <el-form-item label="禁忌话题">
        <el-input v-model.trim="form.forbidden_topics" type="textarea" :rows="3" placeholder="例如：暴力细节、仇恨言论等" />
      </el-form-item>
      <el-form-item label="口头禅">
        <el-input v-model.trim="form.catchphrases" placeholder="例如：慢慢来，我在" />
      </el-form-item>
      <el-form-item label="系统提示词">
        <el-input v-model.trim="form.system_prompt" type="textarea" :rows="4" placeholder="更细粒度约束角色行为的提示词" />
      </el-form-item>
      <el-form-item label="启用AB提示词">
        <el-switch v-model="form.prompt_ab_enabled" />
        <el-text type="info">开启后按用户ID稳定分流到提示词A/B。</el-text>
      </el-form-item>
      <el-form-item label="提示词A">
        <el-input v-model.trim="form.prompt_a" :disabled="!form.prompt_ab_enabled" type="textarea" :rows="3" placeholder="A版本提示词" />
      </el-form-item>
      <el-form-item label="提示词B">
        <el-input v-model.trim="form.prompt_b" :disabled="!form.prompt_ab_enabled" type="textarea" :rows="3" placeholder="B版本提示词" />
      </el-form-item>
      <el-form-item label="B流量比例(0-100)">
        <el-input v-model.number="form.prompt_b_ratio" :disabled="!form.prompt_ab_enabled" type="number" placeholder="50" />
      </el-form-item>
      <el-form-item label="标签">
        <el-input v-model.trim="form.tags" placeholder="逗号分隔，如 温柔,治愈,陪伴" />
        <el-text type="info">用途：用于分类、搜索、提示词补充，建议 3-6 个关键词。</el-text>
      </el-form-item>
      <el-form-item label="公开可见">
        <el-switch v-model="form.is_public" />
        <el-text type="info">开启：前端所有用户都能看到该角色。</el-text>
      </el-form-item>
      <el-form-item v-if="!form.is_public" label="指定可见用户ID">
        <el-select
          v-model="form.visible_user_ids"
          multiple
          filterable
          remote
          reserve-keyword
          :remote-method="searchUsers"
          :loading="userSearchLoading"
          placeholder="搜索手机号/昵称并选择用户"
          style="width: 100%"
        >
          <el-option
            v-for="u in userOptions"
            :key="u.id"
            :label="userOptionLabel(u)"
            :value="u.id"
          />
        </el-select>
        <el-text type="info">关闭公开后，仅下拉中选中的用户可见该角色。</el-text>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { adminApi } from '../api/admin'

const keyword = ref('')
const list = ref([])
const total = ref(0)
const loading = ref(false)
const dialogVisible = ref(false)
const editId = ref(0)
const userOptions = ref([])
const userSearchLoading = ref(false)
const query = ref({
  page: 1,
  page_size: 20
})
const form = ref({
  name: '',
  gender: 'other',
  avatar: '',
  description: '',
  tags: '',
  system_prompt: '',
  opening_message: '',
  speaking_style: '',
  tts_voice_type: '',
  forbidden_topics: '',
  boundary_rules: '',
  catchphrases: '',
  prompt_ab_enabled: false,
  prompt_a: '',
  prompt_b: '',
  prompt_b_ratio: 50,
  is_public: true,
  visible_user_ids: []
})

const categoryMap = {
  male: '男性角色',
  female: '女性角色',
  other: '其他'
}

async function load() {
  loading.value = true
  try {
    const data = await adminApi.roles({
      keyword: keyword.value,
      page: query.value.page,
      page_size: query.value.page_size
    })
    list.value = data.list || []
    total.value = data.total || 0
  } catch (e) {
    ElMessage.error(e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

function onSearch() {
  query.value.page = 1
  load()
}

function onReset() {
  keyword.value = ''
  query.value.page = 1
  load()
}

function onPageChange(page) {
  query.value.page = page
  load()
}

function openCreate() {
  editId.value = 0
  form.value = {
    name: '',
    gender: 'female',
    avatar: '',
    description: '',
    tags: '',
    system_prompt: '',
    opening_message: '',
    speaking_style: '',
    tts_voice_type: '',
    forbidden_topics: '',
    boundary_rules: '',
    catchphrases: '',
    prompt_ab_enabled: false,
    prompt_a: '',
    prompt_b: '',
    prompt_b_ratio: 50,
    is_public: true,
    visible_user_ids: []
  }
  searchUsers('')
  dialogVisible.value = true
}

async function openEdit(row) {
  const selectedIds = Array.isArray(row.visible_user_ids) ? row.visible_user_ids : []
  editId.value = row.role_id
  form.value = {
    name: row.name || '',
    gender: row.gender || 'female',
    avatar: row.avatar || '',
    description: row.description || '',
    tags: row.raw_tags || '',
    system_prompt: row.system_prompt || '',
    opening_message: row.opening_message || '',
    speaking_style: row.speaking_style || '',
    tts_voice_type: row.tts_voice_type || '',
    forbidden_topics: row.forbidden_topics || '',
    boundary_rules: row.boundary_rules || '',
    catchphrases: row.catchphrases || '',
    prompt_ab_enabled: !!row.prompt_ab_enabled,
    prompt_a: row.prompt_a || '',
    prompt_b: row.prompt_b || '',
    prompt_b_ratio: typeof row.prompt_b_ratio === 'number' ? row.prompt_b_ratio : 50,
    is_public: row.is_public !== false,
    visible_user_ids: selectedIds
  }
  await ensureUserOptionsByIDs(selectedIds)
  dialogVisible.value = true
}

function categoryText(v) {
  return categoryMap[v] || '其他'
}

function fullAvatar(url) {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `https://friend-api.martinself.cn${url}`
}

function userOptionLabel(user) {
  const nickname = (user.nickname || '').trim()
  const phone = (user.phone || '').trim()
  if (nickname && phone) return `${nickname} (${phone}) [ID:${user.id}]`
  if (nickname) return `${nickname} [ID:${user.id}]`
  if (phone) return `${phone} [ID:${user.id}]`
  return `用户${user.id}`
}

function mergeUserOptions(items) {
  const map = new Map((userOptions.value || []).map((u) => [u.id, u]))
  ;(items || []).forEach((u) => {
    if (!u || !u.id) return
    map.set(u.id, u)
  })
  userOptions.value = Array.from(map.values())
}

async function searchUsers(keyword) {
  userSearchLoading.value = true
  try {
    const data = await adminApi.users({
      keyword: (keyword || '').trim(),
      page: 1,
      page_size: 30
    })
    mergeUserOptions(data.list || [])
  } catch (e) {
    ElMessage.error(e.message || '搜索用户失败')
  } finally {
    userSearchLoading.value = false
  }
}

async function ensureUserOptionsByIDs(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return
  userSearchLoading.value = true
  try {
    const data = await adminApi.users({
      ids: ids.join(','),
      page: 1,
      page_size: Math.max(ids.length, 20)
    })
    mergeUserOptions(data.list || [])
  } catch (e) {
    ElMessage.error(e.message || '加载可见用户失败')
  } finally {
    userSearchLoading.value = false
  }
}

async function onAvatarSelect(uploadFile) {
  const raw = uploadFile && uploadFile.raw
  if (!raw) return
  const formData = new FormData()
  formData.append('file', raw)
  try {
    const data = await adminApi.uploadRoleAvatar(formData)
    form.value.avatar = data.url || ''
    ElMessage.success('头像上传成功')
  } catch (e) {
    ElMessage.error(e.message || '头像上传失败')
  }
}

async function save() {
  const payload = {
    name: form.value.name,
    gender: form.value.gender,
    avatar: form.value.avatar,
    description: form.value.description,
    tags: form.value.tags.split(',').map((v) => v.trim()).filter(Boolean),
    system_prompt: form.value.system_prompt,
    opening_message: form.value.opening_message,
    speaking_style: form.value.speaking_style,
    tts_voice_type: form.value.tts_voice_type,
    forbidden_topics: form.value.forbidden_topics,
    boundary_rules: form.value.boundary_rules,
    catchphrases: form.value.catchphrases,
    prompt_ab_enabled: !!form.value.prompt_ab_enabled,
    prompt_a: form.value.prompt_a,
    prompt_b: form.value.prompt_b,
    prompt_b_ratio: Number(form.value.prompt_b_ratio || 0),
    is_public: !!form.value.is_public,
    visible_user_ids: form.value.is_public ? [] : (form.value.visible_user_ids || [])
  }
  try {
    if (editId.value) {
      await adminApi.updateRole(editId.value, payload)
    } else {
      await adminApi.createRole(payload)
    }
    ElMessage.success('保存成功')
    dialogVisible.value = false
    load()
  } catch (e) {
    ElMessage.error(e.message || '保存失败')
  }
}

async function remove(id) {
  try {
    await ElMessageBox.confirm(`确认删除角色 #${id} 吗？`, '删除确认', { type: 'warning' })
    await adminApi.deleteRole(id)
    ElMessage.success('删除成功')
    load()
  } catch (e) {
    if (e === 'cancel') return
    ElMessage.error(e.message || '删除失败')
  }
}

onMounted(load)
</script>
